/**
 * HTTP client (W-X-01 / W-X-02 / W-X-03 / W-X-04).
 *
 * - Auth: bearer token on every request (auth mode resolved from the auth store).
 * - Session (XC-03): a request rejected with 401 UNAUTHORIZED — the session lapsed or was revoked —
 *   renews the token pair once with the refresh token (single flight) and is retried. When renewal
 *   is refused the tokens are dropped and `onSessionExpired` listeners run (AuthContext signs the
 *   user out with a message), instead of every later request failing with a toast.
 * - Error envelope: non-2xx responses are parsed and thrown as `ApiError`.
 * - Idempotency: pass `idempotencyKey` on mutating calls marked 🔑 in api-contract.md.
 * - Input hygiene: string fields in JSON / multipart bodies are trimmed (passwords excepted),
 *   so padded or whitespace-only input never reaches the backend (see lib/form.ts).
 *
 * Typed request/response shapes come from `schema.ts` (generated). This thin wrapper
 * owns transport concerns only.
 */

import { ApiError, isApiErrorEnvelope } from "./errors";
import {
  clearTokens,
  getAccessToken,
  getAuthMode,
  getRefreshToken,
  setTokens,
  syncTokensFromStorage,
} from "../auth/tokenStore";
import { trimFormData, trimStrings } from "../lib/form";

// Resolution order: runtime config (window.__APP_CONFIG__ from /env.js — Cloudflare Pages
// Function in prod, Vite middleware in dev) → build-time .env value → same-origin. The
// runtime value lets the Cloudflare dashboard variable bind without a rebuild (see
// functions/env.js); `||` (not `??`) so an empty string falls through to the next source.
const API_BASE =
  window.__APP_CONFIG__?.apiBase || import.meta.env.VITE_API_BASE || "";

export interface RequestOptions {
  method?: string;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

// ----- Session renewal + expiry (XC-03) -----

type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

/** Run `listener` when the session can no longer be used or renewed. Returns an unsubscribe. */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

/** Drop the tokens and tell listeners the session is over (once, however many requests noticed). */
export function expireSession(): void {
  const hadSession = !!getAccessToken() || !!getRefreshToken();
  clearTokens();
  if (hadSession) sessionExpiredListeners.forEach((listener) => listener());
}

// Web sessions renew with the refresh token like the mobile apps do. Build with
// VITE_WEB_SESSION_RENEWAL=false to restore the hard sign-out when the access token lapses
// (LOGIN_SESSION_HOURS on the API).
const RENEWAL_ENABLED = import.meta.env.VITE_WEB_SESSION_RENEWAL !== "false";

export type RenewOutcome = "renewed" | "rejected" | "offline";
let renewing: Promise<RenewOutcome> | null = null;

/**
 * Rotate the token pair with the refresh token. Single flight: rotating the same refresh token
 * twice is rejected by the API (the old session is revoked on rotation), which would sign the
 * user out, so concurrent callers share one attempt.
 */
export function renewSession(): Promise<RenewOutcome> {
  if (!renewing) {
    renewing = (async (): Promise<RenewOutcome> => {
      syncTokensFromStorage(); // use the newest pair, even if another tab rotated it a moment ago
      const refreshToken = getRefreshToken();
      if (!RENEWAL_ENABLED || !refreshToken) return "rejected";
      let res: Response;
      try {
        res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Auth-Mode": getAuthMode(),
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch {
        return "offline"; // couldn't reach the server: the session may still be fine
      }
      if (!res.ok) {
        if (res.status >= 500) return "offline";
        // Another tab may have rotated this refresh token first: then its new pair is already
        // in storage, and the session is fine.
        syncTokensFromStorage();
        const latest = getRefreshToken();
        return latest && latest !== refreshToken ? "renewed" : "rejected";
      }
      const pair = await res.json().catch(() => null);
      if (!pair?.access_token || !pair?.refresh_token) return "rejected";
      setTokens(pair.access_token, pair.refresh_token);
      return "renewed";
    })().finally(() => {
      renewing = null;
    });
  }
  return renewing;
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const mode = getAuthMode();
  headers["X-Auth-Mode"] = mode;
  if (mode === "bearer") {
    const token = getAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * fetch with the auth headers, renewing a lapsed session once. Auth endpoints are exempt: there a
 * 401 means "those credentials are wrong", not "your session expired". Only a 401 with code
 * UNAUTHORIZED, on a request that carried a token, is treated as a dead session.
 */
async function authedFetch(path: string, init: RequestInit): Promise<Response> {
  const isDeadSession = async (r: Response) => {
    if (r.status !== 401) return false;
    const payload = await r.clone().json().catch(() => null);
    return !isApiErrorEnvelope(payload) || payload.error.code === "UNAUTHORIZED";
  };
  const send = () =>
    fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { ...(init.headers as Record<string, string>), ...authHeaders() },
    });
  const sentToken = getAccessToken();
  let res = await send();
  if (!sentToken || path.startsWith("/api/v1/auth/") || !(await isDeadSession(res))) return res;

  // Another tab may already have renewed the session: retry with its token before rotating.
  syncTokensFromStorage();
  const stored = getAccessToken();
  if (stored && stored !== sentToken) {
    res = await send();
    if (!(await isDeadSession(res))) return res;
  }

  const outcome = await renewSession();
  if (outcome === "renewed") return send();
  if (outcome === "rejected") expireSession();
  return res;
}

async function throwApiError(res: Response, fallbackMessage: string): Promise<never> {
  const payload = await res.json().catch(() => null);
  if (isApiErrorEnvelope(payload)) throw new ApiError(res.status, payload.error);
  throw new ApiError(res.status, {
    code: "INTERNAL_ERROR",
    message: fallbackMessage,
    details: {},
    request_id: res.headers.get("X-Request-ID"),
  });
}

export async function apiFetch<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": "en",
    ...opts.headers,
  };

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(trimStrings(opts.body));
  }
  if (opts.idempotencyKey) {
    headers["Idempotency-Key"] = opts.idempotencyKey;
  }

  const res = await authedFetch(path, {
    method: opts.method ?? "GET",
    headers,
    body,
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;
  if (!res.ok) return throwApiError(res, "Unexpected error.");
  return (await res.json().catch(() => null)) as T;
}

/**
 * Convenience helper for a fresh idempotency key (W-X-04).
 *
 * `crypto.randomUUID()` only exists in a *secure context* (https or http://localhost). When the
 * app is served over plain HTTP on a LAN/Tailscale IP (e.g. http://192.168.x.x:3000 — which we
 * intentionally allow in CORS_ORIGINS for device testing) it's `undefined`, and calling it throws
 * a synchronous TypeError inside the mutationFn — which React Query routes to onError and our
 * `describeError` mis-reports as "Couldn't reach the server." Fall back to a v4 UUID built from
 * `crypto.getRandomValues` (available in insecure contexts too), then to Math.random() as a last
 * resort, so mutating POSTs work regardless of how the page is served.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Set the version (4) and variant (10xx) bits per RFC 4122.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
}

/**
 * Multipart POST (file uploads). Like apiFetch but sends FormData (no Content-Type header
 * so the browser sets the multipart boundary) and applies the same auth + error handling.
 */
export async function rawFormFetch<T>(
  path: string,
  form: FormData,
  opts: { idempotencyKey?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": "en",
  };
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

  // Authorised and renewed like every request; text fields trimmed (passwords aren't).
  const res = await authedFetch(path, { method: "POST", headers, body: trimFormData(form) });
  if (!res.ok) return throwApiError(res, "Upload failed.");
  return (await res.json().catch(() => null)) as T;
}

/**
 * Authenticated download — fetches a binary/text response and triggers a browser save.
 * Used for DPDP export ZIPs and analytics CSVs (auth header can't ride on a plain <a href>).
 */
export async function downloadFile(
  path: string,
  filename: string,
): Promise<void> {
  const res = await authedFetch(path, {});
  if (!res.ok) return throwApiError(res, "Download failed.");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Authenticated binary fetch for *inline display* — returns an object URL (and the response
 * content type) so the caller can render an image/PDF whose endpoint needs the auth header
 * (a plain <img src>/<iframe src> can't send one). The caller MUST revoke the URL when done.
 */
/** The file name in a Content-Disposition header (`filename*=UTF-8''a%20b.pdf` or `filename="a.pdf"`), or "". */
export function dispositionFilename(header: string | null): string {
  if (!header) return "";
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header);
  return plain ? plain[1].trim() : "";
}

export async function fetchBlobUrl(
  path: string,
): Promise<{ url: string; contentType: string; filename: string }> {
  const res = await authedFetch(path, {});
  if (!res.ok) return throwApiError(res, "Failed to load file.");
  const blob = await res.blob();
  const contentType = res.headers.get("Content-Type") ?? blob.type;
  // The server's name keeps a download's extension (PHA-06: a HEIC uploaded before D-12 is
  // served as a download, and saved without one it couldn't be opened).
  const filename = dispositionFilename(res.headers.get("Content-Disposition"));
  // Only images and PDFs may render from a blob URL (ORD-02): a blob of any other type (say an
  // uploaded "rx.html") would run on this app's origin if opened, so it gets a type browsers
  // only ever download.
  const renderable = /^(image\/(jpeg|png|webp|heic|heif|gif)|application\/pdf)\b/i.test(contentType);
  return {
    url: URL.createObjectURL(renderable ? blob : new Blob([blob], { type: "application/octet-stream" })),
    contentType: renderable ? contentType : "application/octet-stream",
    filename,
  };
}
